import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await axios.post(`${backendUrl}/api/auth/login`, {
        email,
        password
      });

      // Save token and login
      localStorage.setItem('access_token', response.data.access_token);
      authLogin(response.data.user);
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle different error types
      if (err.response) {
        // Server responded with error
        const status = err.response.status;
        const message = err.response.data?.detail;
        
        if (status === 401) {
          setError('Invalid email or password');
        } else if (status === 400) {
          setError(message || 'Invalid request');
        } else if (status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(message || 'Login failed');
        }
      } else if (err.request) {
        // Request made but no response
        setError('Cannot connect to server. Please check your connection.');
      } else {
        // Something else happened
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4 py-8">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-mentality-teal/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-y-auto max-h-[90vh] my-4">
        {/* Decorative header bar */}
        <div className="h-2 bg-gradient-to-r from-mentality-teal via-cyan-500 to-teal-400"></div>
        
        <div className="p-4">
          {/* Logo */}
          <div className="flex justify-center mb-3">
            <img 
              src="/mentality-logo.jpg" 
              alt="The College Mentality" 
              className="h-24 w-24 object-contain rounded-full shadow-lg"
            />
          </div>

          {/* Title */}
          <div className="text-center mb-3">
            <h1 className="text-xl font-bold text-mentality-teal">
              Welcome Back
            </h1>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-2">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-2 py-1.5 rounded-r-lg">
                <p className="text-xs">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-mentality-teal focus:border-mentality-teal"
                placeholder="name@gsumail.gram.edu"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-mentality-teal focus:border-mentality-teal"
                  placeholder="Password"
                  required
                  minLength="6"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-2 text-gray-400"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                backgroundColor: '#5BB9B8',
                color: 'white',
                fontWeight: 'bold',
                padding: '12px 16px',
                borderRadius: '8px',
                border: 'none',
                marginTop: '12px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? 'Signing In...' : 'SIGN IN'}
            </button>
          </form>

          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <p style={{ fontSize: '12px', color: '#666' }}>
              No account?{' '}
              <Link to="/register" style={{ color: '#5BB9B8', fontWeight: '600', textDecoration: 'none' }}>
                Create Account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
